<!-- eslint-disable vue/html-self-closing -->
<template>
  <div class="project-commit-form">
    <div class="project-commit-form__body">
      <div
        v-if="formValidationErrors && Object.keys(formValidationErrors)?.length > 0"
        class="project-commit-form__body-errors mb-3"
      >
        <gf-custom-alert
          v-for="(error, index) in formatedErrors"
          :key="index"
          variant="danger"
          class="mb-2"
        >
          <template #body>
            <b>
              <i class="gf-icon gf-icon-alert mr-2"></i>
              <span v-html="error"></span>
            </b>
          </template>
        </gf-custom-alert>
      </div>

      <div class="form-group">
        <label
          class="font-weight-bold"
          for="files"
        >
          {{ $t('file.files') }}
          <span class="font-weight-normal text-muted">({{ $t('file.sizeMessage') }})</span>
        </label>
        <gf-custom-multi-file-uploader
          ref="gfMultiFileUploader"
          v-model="form.filesData"
          class="p-3 bg-white rounded"
          uploader-id="customFile"
          uploader-name="files"
          :max-files-size="100000000"
        ></gf-custom-multi-file-uploader>
      </div>
      <div class="form-group">
        <label
          class="font-weight-bold"
          for="commitMessage"
        >
          {{ $t('commit.message') }}
        </label>
        <textarea
          id="commitMessage"
          v-model="form.message"
          rows="4"
          class="form-control form-control-sm"
          :class="{ 'border-danger': formErrors.message }"
          type="text"
          inputmode="text"
          :placeholder="$t('commit.message')"
          @input="onCommitMessageInput"
        ></textarea>
      </div>
      <div class="project-commit-form__body-errors mb-3" 
           v-if="!branchPermissions?.ALLOW_TO_PUSH_IN_CURRENT_BRANCH && branchPermissions?.ALLOW_TO_PUSH_IN_CURRENT_BRANCH !== undefined"
      >
        <gf-custom-alert
          variant="danger"
          class="mb-2"
        >
          <template #body>
            <span v-html="$t('commit.banPush', {currentBranch: branch})"></span>
          </template>
        </gf-custom-alert>
      </div>
      <div
        class="project-commit-form__body-branch form-group"
        v-if="!isNewRepo"
      >
        <label class="gf-custom-radio">
          <input
            :disabled="!branchPermissions?.ALLOW_TO_PUSH_IN_CURRENT_BRANCH"
            v-model="form.isDirectCommit"
            type="radio"
            name="uploadFilesCommitRadio"
            :value="true"
          />
          <span :class="{'text-muted': !branchPermissions?.ALLOW_TO_PUSH_IN_CURRENT_BRANCH}">
            {{ $t('commit.directlyInBranch') }}
            <code class="ml-1">{{ branch }}</code>
          </span>
        </label>
        <label class="gf-custom-radio w-100">
          <input
            v-model="form.isDirectCommit"
            type="radio"
            name="uploadFilesCommitRadio"
            :value="false"
          />
          <div class="w-100">
            <div class="w-100">
              {{ $t('commit.inNewBranch') }}
              <br />
              <label
                v-if="!form.isDirectCommit"
                class="gf-counted-input"
                for="newBranchName"
                :data-current-length="form.newBranchName?.length + ' / 250'"
              >
                <input
                  v-model="form.newBranchName"
                  id="newBranchName"
                  type="text"
                  minlength="1"
                  maxlength="250"
                  class="form-control form-control-sm w-100 mt-2"
                  :class="{ 'border-danger': formErrors.newBranchName }"
                  :placeholder="$t('message.branch_name')"
                  @input="onCommitBranchInput"
                />
              </label>
            </div>
          </div>
        </label>
      </div>
      <div
        v-else
        class="project-commit-form__body-branch form-group"
      >
        <div class="w-100">
          <label for="newBranchName" class="font-weight-bolder">{{ $t('commit.inNewBranch') }}</label>
          <label
            class="gf-counted-input"
            for="newBranchName"
            :data-current-length="form.newBranchName?.length + ' / 250'"
          >
            <input
              v-model="form.newBranchName"
              id="newBranchName"
              type="text"
              minlength="1"
              maxlength="250"
              class="form-control form-control-sm w-100"
              :class="{ 'border-danger': formErrors.newBranchName }"
              :placeholder="$t('message.branch_name')"
              @input="onCommitBranchInput"
            />
          </label>
        </div>
      </div>
    </div>
    <div class="project-commit-form__footer">
      <button
        type="button"
        class="btn btn-dark btn-sm m-1"
        @click="cancel"
      >
        {{ $t('message.cancel') }}
      </button>
      <button
        type="button"
        class="btn btn-success btn-sm m-1"
        :disabled="checkFormErrors"
        @click="submit"
      >
        {{ $t('file.uploadFiles') }}
      </button>
    </div>
  </div>
</template>

<script>
import { FORM_NAME_ENUMS } from '@shared/variables/form-name.enums';
import GfCustomAlert from '@/shared-modules/shared-components/shared/GfCustomAlert';
// eslint-disable-next-line max-len
import GfCustomMultiFileUploader from '@/shared-modules/shared-components/shared/GfCustomMultiFileUploader';
import projectFilesMessages from '@/shared-modules/modules/project-files/project-files.messages';
import HelperS from '@/shared-modules/services/helper.service';

export default {
  name: 'ProjectUploadFilesForm',
  components: { GfCustomAlert, GfCustomMultiFileUploader },
  props: {
    branch: {
      type: String,
      required: true
    },
    isNewRepo: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: {
        filesData: {},
        message: '',
        isDirectCommit: true,
        newBranchName: ''
      },
      formErrors: {
        message: false,
        newBranchName: false
      }
    };
  },
  computed: {
    autoCommitMessage() {
      if (this.form?.filesData?.files?.length > 1) {
        const fileNames = [];
        this.form?.filesData?.files?.forEach((file) => {
          fileNames.push(file?.name);
        });
        // eslint-disable-next-line no-unsafe-optional-chaining
        return this.$t('file.addedFiles') + fileNames?.join(', ');
      } else if (this.form?.filesData?.files?.length === 1) {
        // eslint-disable-next-line no-unsafe-optional-chaining
        return this.$t('file.added') + this.form?.filesData?.files[0]?.name;
      }
      return '';
    },
    checkFormMessageErrors() {
      return this.form.message.length === 0;
    },
    checkFormBranchErrors() {
      return this.form.isDirectCommit ? false : this.form.newBranchName?.length === 0;
    },
    checkFormFilesErrors() {
      return this.form?.filesData?.isMaxSizeExceeded || this.form?.filesData?.files?.length === 0;
    },
    checkFormErrors() {
      return (
        this.checkFormMessageErrors ||
        this.checkFormBranchErrors ||
        this.checkFormFilesErrors ||
        Object.keys(this.form.filesData).length === 0
      );
    },
    formValidationErrors() {
      return this.$store.getters.getFormErrors(FORM_NAME_ENUMS.UPLOAD_FILES_FORM);
    },
    formatedErrors() {
      return HelperS.destructureObject(this?.formValidationErrors);
    },
    branchPermissions() {
      return (
        this.$store.getters.getBranchPermissionByBranch(this.branch)
      )
    }
  },
  watch: {
    'form.filesData': function () {
      this.form.message = this.autoCommitMessage;
    }
  },
  mounted() {
    this.form.message = this.autoCommitMessage;

    if (this.isNewRepo || !this.branchPermissions?.ALLOW_TO_PUSH_IN_CURRENT_BRANCH && this.branchPermissions?.ALLOW_TO_PUSH_IN_CURRENT_BRANCH !== undefined) {
      this.form.isDirectCommit = false
    }
  },
  methods: {
    cancel() {
      this.$emit('cancel');
    },
    submit() {
      this.$emit('submit', this.form);
    },
    onCommitMessageInput() {
      this.formErrors.message = this.checkFormMessageErrors;
    },
    onCommitBranchInput() {
      this.formErrors.newBranchName = this.checkFormBranchErrors;
    },
    resetFiles() {
      this.$refs.gfMultiFileUploader.resetFiles();
    }
  },
  i18n: { messages: projectFilesMessages }
};
</script>

<style lang="scss">
$this: '.project-commit-form';

#{$this} {
  #{$this}__body {
    &-branch {
      display: flex;
      flex-direction: column;

      & code {
        margin-top: 2px;
      }
    }
  }
  #{$this}__footer {
    display: flex;
    justify-content: flex-end;
  }
}
</style>
